/**
 * Date utility functions for order management
 */

/**
 * Check if a date is a business day (Monday-Friday)
 * @param date - Date to check
 * @returns True if date is a business day
 */
export const isBusinessDay = (date: Date): boolean => {
  const day = date.getDay()
  return day >= 1 && day <= 5 // Monday = 1, Friday = 5
}

/**
 * Add business days to a date (skipping weekends)
 * @param date - Starting date
 * @param days - Number of business days to add
 * @returns New date with business days added
 */
export const addBusinessDays = (date: Date, days: number): Date => {
  const result = new Date(date)
  let remainingDays = days
  
  while (remainingDays > 0) {
    result.setDate(result.getDate() + 1)
    if (isBusinessDay(result)) {
      remainingDays--
    }
  }
  
  return result
}

/**
 * Calculate delivery estimate (2 business days from creation, or 3 if created after 2pm)
 * @param createdDate - Order creation date (ISO string)
 * @returns Delivery estimate date (ISO string)
 */
export const calculateDeliveryEstimate = (createdDate: string): string => {
  const created = new Date(createdDate)
  const hour = created.getHours()
  
  // If created after 2pm, add an extra day
  const daysToAdd = hour >= 14 ? 3 : 2
  
  const deliveryDate = addBusinessDays(created, daysToAdd)
  return deliveryDate.toISOString().split('T')[0]
}

/**
 * Format date to YYYY-MM-DD
 * @param date - Date to format
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toISOString().split('T')[0]
}

/**
 * Format date for display (MM/DD/YYYY)
 * @param date - Date to format
 * @returns Formatted date string for display
 */
export const formatDisplayDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US')
}

/**
 * Get current date in YYYY-MM-DD format
 * @returns Current date string
 */
export const getCurrentDate = (): string => {
  return formatDate(new Date())
} 